import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import useOfferUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { EFeatureFlag } from 'constants/enums';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { EToasterStatus, showToast } from 'utils/showToast';
import { BUTTON_TEXT, OFFER_TYPE_FILTER_OPTIONS, ROUTE_SELECT_TYPE } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignRow, OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';
import { resolveOfferDesignType } from 'views/OfferDesign/OfferDesign.utils';

import DialogModal from 'components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from 'components/DialogModalManager/constants/modalConfigs';
import PortalHeader from 'components/PortalHeader/PortalHeader';

import { Filters } from './components/Filters/Filters';
import { TableContent } from './components/TableContent/TableContent';
import { useOfferDesignColumns } from './hooks/useOfferDesignColumns';
import { useOfferDesignFilters } from './hooks/useOfferDesignFilters';

const OfferDesignTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { getOffersUI, deleteOfferUI } = useOfferUI(publisherId);
  const { canUserEdit } = usePermissions();
  const { isActionEnabled } = useUserPermissions();
  const { getFeatureFlagByKey } = useFeatureFlags({ publisherId });

  const isTriggeredPopupEnabled = getFeatureFlagByKey(EFeatureFlag.DASHBOARD_DAILY_BONUS_SIDE_MENU);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOfferUI, setSelectedOfferUI] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    };
  }, [isDeleteModalOpen]);

  const canManage = isActionEnabled(
    null,
    EPermissionGroup.OFFER_DESIGN,
    EPermissionAction.MANAGE,
    publisherId
  );

  const offers: OfferDesignRow[] = useMemo(() => {
    if (getOffersUI.isLoading || !getOffersUI.data) return [];
    const result: OfferDesignRow[] = getOffersUI.data.result;
    if (isTriggeredPopupEnabled) return result;
    return result.filter((o) => resolveOfferDesignType(o.offerUiType, o.offerUiSubType) !== OfferDesignTypes.TriggeredPopup);
  }, [getOffersUI.data, getOffersUI.isLoading, isTriggeredPopupEnabled]);

  const typeFilterOptions = useMemo(
    () => isTriggeredPopupEnabled
      ? OFFER_TYPE_FILTER_OPTIONS
      : OFFER_TYPE_FILTER_OPTIONS.filter((o) => o.value !== OfferDesignTypes.TriggeredPopup),
    [isTriggeredPopupEnabled]
  );

  const {
    externalIdFilter,
    nameFilter,
    typeFilter,
    filteredOffers,
    handleExternalIdChange,
    handleNameChange,
    handleTypeChange,
    handleClearFilters
  } = useOfferDesignFilters(offers);

  const actions = useMemo(
    () => ({
      onEdit: (id: string, offerUiType: string, offerUiSubType?: string) =>
        navigate(`./form/${id}?type=${resolveOfferDesignType(offerUiType, offerUiSubType)}`),
      onDuplicate: (id: string, offerUiType: string, offerUiSubType?: string) =>
        navigate(`./dup/${id}?type=${resolveOfferDesignType(offerUiType, offerUiSubType)}`),
      onDelete: (id: string) => {
        setSelectedOfferUI(id);
        setIsDeleteModalOpen(true);
      }
    }),
    [navigate]
  );

  const permissions = useMemo(
    () => ({ canUserEdit, canManage }),
    [canUserEdit, canManage]
  );

  const { columns } = useOfferDesignColumns({
    isLoading: getOffersUI.isLoading,
    actions,
    permissions
  });

  const handleCreate = () => navigate(`./${ROUTE_SELECT_TYPE}`);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleDeleteOfferUI = useCallback(async () => {
    if (!selectedOfferUI) return;
    await deleteOfferUI.mutate(selectedOfferUI, {
      onSuccess: () => {
        showToast({
          message: 'Offer Design deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffersUI.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete Offer Design',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setIsDeleteModalOpen(false);
        setSelectedOfferUI(null);
      }
    });
  }, [selectedOfferUI, deleteOfferUI, getOffersUI]);

  return (
    <div className="overflow-y-scroll opacity-100 pointer-events-auto">
      <PortalHeader
        title="Offer Design"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={handleCreate}
        primaryButtonState={{
          disabled: !canUserEdit,
          hidden: !canManage
        }}
        isCreateButton
      />
      {offers.length > 0 && (
        <Filters
          externalIdFilter={externalIdFilter}
          nameFilter={nameFilter}
          typeFilter={typeFilter}
          typeFilterOptions={typeFilterOptions}
          onExternalIdChange={handleExternalIdChange}
          onNameChange={handleNameChange}
          onTypeChange={handleTypeChange}
          onClearFilters={handleClearFilters}
        />
      )}
      <TableContent
        isLoading={getOffersUI.isLoading || getOffersUI.isFetching}
        offers={offers}
        filteredOffers={filteredOffers}
        columns={columns}
        canManage={canManage}
        onCreate={handleCreate}
      />
      {isDeleteModalOpen && (
        <DialogModal
          isOpen={isDeleteModalOpen}
          onOpenChange={handleCloseDeleteModal}
          title={modalConfigs[PORTAL_MODAL_NAMES.DELETE_OFFER_UI_POPUP].title}
          description={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_OFFER_UI_POPUP].description
          }
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteOfferUI },
            onClose: {
              label: 'Cancel',
              action: handleCloseDeleteModal,
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default OfferDesignTable;
